import React from 'react';
import { useTable, usePagination, useSortBy } from 'react-table';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Gym } from '@t/gym';
import Button from '@components/Button';
import LoaderVertical from '@components/LoaderVertical';
import { UserRole } from '@t/user';
import { getUser } from '@redux/modules/auth/selectors';

import styles from './styles.module.css';

export interface Props {
    gyms: Gym[];
    loading: boolean;
}

const columns = [
    {
        Header: 'Название',
        accessor: 'title',
        Cell: ({ cell: { value } }: any) => <Button>{value}</Button>,
    },
    {
        Header: 'Адрес фитнес-площадки',
        accessor: (gym: Gym) => gym.address || 'Не указан',
    },
    {
        Header: 'Цена',
        accessor: (gym: Gym) => {
            if (gym.hasPerMinWorkouts) {
                return gym.pricePerMinRub > 0
                    ? `${gym.pricePerMinRub}₽/мин.`
                    : ' ';
            } else if (gym.hasSingleWorkouts) {
                return gym.singleWorkoutPriceInfo.weekDayOffPeakPrice
                    ? `${gym.singleWorkoutPriceInfo.weekDayOffPeakPrice}₽`
                    : `${gym.singleWorkoutPriceInfo.weekDayPrice}₽`;
            } else if (gym.hasGroupWorkouts) {
                return gym.minGroupWorkoutPrice
                    ? `${gym.minGroupWorkoutPrice}₽`
                    : ' ';
            }
        },
    },
    {
        Header: 'Авторизован',
        accessor: (gym: Gym) => (gym.authorized ? 'Да' : 'Нет'),
    },
];

const columnsForAdmin = [
    {
        Header: 'Название',
        accessor: 'title',
        Cell: ({ cell: { value } }: any) => <Button>{value}</Button>,
    },
    {
        Header: 'Адрес фитнес-площадки',
        accessor: (gym: Gym) => gym.address || 'Не указан',
    },
    {
        Header: 'Цена',
        accessor: (gym: Gym) => {
            if (gym.hasPerMinWorkouts) {
                return gym.pricePerMinRub > 0
                    ? `${gym.pricePerMinRub}₽/мин.`
                    : ' ';
            } else if (gym.hasSingleWorkouts) {
                return gym.singleWorkoutPriceInfo.weekDayOffPeakPrice
                    ? `${gym.singleWorkoutPriceInfo.weekDayOffPeakPrice}₽`
                    : `${gym.singleWorkoutPriceInfo.weekDayPrice}₽`;
            } else if (gym.hasGroupWorkouts) {
                return gym.minGroupWorkoutPrice
                    ? `${gym.minGroupWorkoutPrice}₽`
                    : ' ';
            }
        },
    },
    {
        Header: 'Авторизован',
        accessor: (gym: Gym) => (gym.authorized ? 'Да' : 'Нет'),
    },
];

export const GymListTable: React.FC<Props> = ({ gyms, loading }) => {
    const user = useSelector(getUser);
    const isSuperAdmin = user?.role === UserRole.SuperAdmin;

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
    } = useTable(
        {
            // @ts-ignore
            columns: isSuperAdmin ? columnsForAdmin : columns,
            data: gyms || [],
            initialState: { pageIndex: 0, pageSize: 10000 },
        },
        useSortBy,
        usePagination
    );
    const history = useHistory();

    return (
        <>
            <table {...getTableProps()} className={styles.table}>
                <thead>
                    {headerGroups.map((headerGroup) => (
                        <tr
                            {...headerGroup.getHeaderGroupProps()}
                            key={Math.random()}
                        >
                            {headerGroup.headers.map((column) => (
                                <th
                                    {...column.getHeaderProps(
                                        column.getSortByToggleProps()
                                    )}
                                    key={Math.random()}
                                >
                                    {column.render('Header')}
                                    <span className={styles.sorted}>
                                        <FontAwesomeIcon
                                            icon={
                                                column.isSorted
                                                    ? column.isSortedDesc
                                                        ? 'sort-up'
                                                        : 'sort-down'
                                                    : 'sort'
                                            }
                                        />
                                    </span>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                    {page.map((row) => {
                        prepareRow(row);
                        const onClick = () => {
                            const gym = row.original;
                            history.push(`/dashboard/gyms/${gym._id}`);
                        };
                        return (
                            <tr
                                className={styles.tableRow}
                                onClick={onClick}
                                {...row.getRowProps()}
                                key={Math.random()}
                            >
                                {row.cells.map((cell, idx) => {
                                    if (idx === 0) {
                                        return (
                                            <td {...cell.getCellProps()}>
                                                {cell.render('Cell')}
                                            </td>
                                        );
                                    }
                                    return (
                                        <td
                                            {...cell.getCellProps()}
                                            key={Math.random()}
                                        >
                                            {cell.render('Cell')}
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {loading && <LoaderVertical />}
        </>
    );
};

export default GymListTable;
